/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormikHelpers } from 'formik';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import {
    FormStepStatusDto,
    RequestForQuoteClient, RequestForQuoteSummary,
} from '../../api/web-api-client';
import { tokenRequest } from '../../authentication/authConfig';
import { ErrorType, WizardFormStepValues, WizardStepProps } from '../../components/forms/WizardForm/types';
import { AccountDetails } from '../../authentication/accountContext';
import AppLogger from '../../instrumentation/AppLogger';
import { formatBannerTitle } from '../common/helperFunctions';

const loadSummary = (id: string, accounts: AccountInfo[], instance: IPublicClientApplication) => async (
    abortSignal?: AbortSignal,
) => {
    if (accounts.length > 0) {
        try {
            const client = new RequestForQuoteClient();
            const tokenResult = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0],
            });
            client.setAuthToken(tokenResult.accessToken);
            const summaryStep = await client.getSummary(id, abortSignal);
            const wizardStepValues: WizardFormStepValues<RequestForQuoteSummary> = {
                stepValues: { ...summaryStep },
            };
            return wizardStepValues;
        } catch (error) {
            AppLogger.error('Failed to load T&C summary details', error as Error, { Id: id });
            throw Error(`Failed to load T&C summary details. Id: ${id}`);
        }
    } else {
        AppLogger.verbose('There are no accounts available to load T&C summary details', { Id: id, Accounts: accounts });
        throw Error(`There are no accounts available to load T&C summary details. Id: ${id}`);
    }
};

const getRedirectionLocationOnError = (id: string) => (errorCode: number, errorType: ErrorType) => '/not-found';

const submitForm = (
    id: string,
    accounts: AccountInfo[],
    instance: IPublicClientApplication,
    isComplete: boolean,
) => async (
    values: RequestForQuoteSummary,
    isDirty: boolean,
    _: FormikHelpers<RequestForQuoteSummary>,
    abortSignal?: AbortSignal,
) => {
    if (accounts.length > 0) {
        try {
            const client = new RequestForQuoteClient();
            const tokenResult = await instance.acquireTokenSilent({
                ...tokenRequest,
                account: accounts[0],
            });
            client.setAuthToken(tokenResult.accessToken);
            AppLogger.verbose(`Submitting summary form for T&C. Id: ${id}`, { values, isComplete });
            await client.submit(
                id,
                {
                    applicationId: id,
                    formStep: values,
                    isCompletingStep: true,
                },
                abortSignal,
            );
        } catch (error) {
            AppLogger.error('Failed to submit summary form for T&C', error as Error, { Id: id });
            throw Error(`Failed to submit summary form for T&C. Id: ${id}`);
        }
    } else {
        AppLogger.verbose('There are no accounts available to save T&C summary details', { Id: id, Accounts: accounts });
        throw Error(`There are no accounts available to save T&C summary details. Id: ${id}`);
    }
};

const requestForQuoteSummaryProps = (
    id: string,
    accounts: AccountInfo[],
    instance: IPublicClientApplication,
    accountDetails: AccountDetails,
    statuses: FormStepStatusDto[],
    bannerTitle: string,
)
: WizardStepProps<RequestForQuoteSummary> => ({
    initialValues: {
    },
    stepStatuses: statuses,
    loadStepValues: loadSummary(id, accounts, instance),
    location: '/summary',
    title: 'Summary and submit',
    hidingFields: {
        organisationAndContact: {
            branchOrLocationName: (x) => !x.organisationAndContact?.branchOrLocationName,
            principalContactHide: (x) => false,
            contact: {
                titleOther: (x) => x.organisationAndContact?.contact?.title !== 'Other',
            },
        },
    },
    onSaveAndNext: submitForm(id, accounts, instance, false),
    bannerTitle,
    bannerRefTitle: `Ref ID: ${id}`,
    bannerSubTitle: formatBannerTitle(accountDetails),
    getRedirectionLocationOnError: getRedirectionLocationOnError(id),
    isSummaryPage: true,
});

export default requestForQuoteSummaryProps;
